import React, { useState } from 'react';
import { Button } from 'framework/ui/mui-builder';
import { Modal } from 'framework/ui/ui-builder';
import { EnumDropdown, FormFieldsData, ModalForm } from 'framework/form';
import { COMMERCIAL_STATE_OPTIONS, useNewProjectService } from 'domain/shared/newProject';
import useReconcileProjectNotifications, {
  PROJECT_NOTIFICATION_SCOPE,
} from '../../shared/notifications/useReconcileProjectNotifications';
import { ControlData } from '../types';

type CommercialStateActionProps = {
  data: ControlData;
  refetchControlsData: () => Promise<unknown>;
};

export const CommercialStateUpdateAction = ({ data, refetchControlsData }: CommercialStateActionProps) => {
  const [openModal, setOpenModal] = useState(false);
  const { updateState, commercialStateValidationSchema } = useNewProjectService();
  const reconcileNotifications = useReconcileProjectNotifications();

  const submitCommercialStateFormFunc = async (stateData: FormFieldsData) => {
    const result = await updateState(data.id, {
      commercialState: stateData.projectDimension?.commercialState,
    });
    await reconcileNotifications(String(data.id), [PROJECT_NOTIFICATION_SCOPE.COMMERCIAL_STATE]).catch(() => undefined);
    return result;
  };

  return (
    <>
      <Button variant="contained" onClick={() => setOpenModal(true)}>
        Define
      </Button>
      <Modal isOpen={openModal} onToggle={() => setOpenModal(false)} title="Commercial state update">
        <ModalForm
          closeModal={() => setOpenModal(false)}
          modalOpen={openModal}
          submitFormFunc={submitCommercialStateFormFunc}
          getValidationSchemaFunc={commercialStateValidationSchema}
          afterSubmit={{ execute: () => refetchControlsData() }}
          getDataFunc={async () => data}
        >
          <EnumDropdown
            options={COMMERCIAL_STATE_OPTIONS}
            source="projectDimension.commercialState"
            label="Commercial state"
          />
        </ModalForm>
      </Modal>
    </>
  );
};

export const CommercialStateReviewAction = ({ data, refetchControlsData }: CommercialStateActionProps) => {
  const { patchProjectCommercialState } = useNewProjectService();
  const reconcileNotifications = useReconcileProjectNotifications();
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await patchProjectCommercialState(data.id);
      await reconcileNotifications(String(data.id), [PROJECT_NOTIFICATION_SCOPE.COMMERCIAL_STATE]).catch(
        () => undefined,
      );
      await refetchControlsData();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button variant="contained" onClick={handleConfirm} loading={isLoading}>
      Confirm
    </Button>
  );
};
